import * as Sentry from "@sentry/react";
// Via the alias, not "../axiosWrapper": this module is also bundled by mobile, where
// @AxiosWrapper resolves to mobile's own wrapper. Importing the web one relatively drags
// web's ProviderApp/App/Router into the Metro bundle and uses the wrong store there.
import axiosWrapper, { type ApiPostResponse } from "@AxiosWrapper";
import { hasModelTreeKey, resolveFileVersionIds } from "./resolveFileVersionIds";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import { errorCodes } from "@Helpers/errorCodes";
// Recipient share view (CP-283).
import { getShareViewerSession } from "@Features/external-share/shareViewerSession";
import { getSharedFileAccess } from "@Features/external-share/sharedLink.api";

/**
 * The stored isFile403 result. `Partial` only because the redux slice starts empty
 * — the shape itself is the server's response schema, which is the sole source of
 * truth for it.
 */
export type FileAccessResult = Partial<ApiPostResponse<"/folders/isFile403">>;

// Transmittal-granted access lives in transmittalResourceHasPermission keyed by the
// transmittal's destination space, so both ids must come from the viewer's URL.
//
// Exported because the PDF viewer makes two more permission-bearing calls that need the same
// context: the /folders/isFile403 write check in pdfHelpers and the /fileSystemServer/View byte
// fetch. Both used to send only the file's own ids, which can never match for a recipient (see
// the note at each call site).
export function getTransmittalContext() {
  const params = new URLSearchParams(window.location.search);
  const transmittalId = params.get("transmittalId");
  const spaceId = params.get("spaceId");
  return transmittalId ? { transmittalId, spaceId: spaceId ?? undefined } : undefined;
}

/**
 * The transmittal the viewer page was opened through, as the file reads take it (/fileOperations/getFileDetails,
 * /views/getViewsData, /views/getDefaultView): the page URL's `transmittalId` when it is well formed, else none. A
 * recipient reads the file's record and its saved views only through it. Mobile runs this too (its file manager opens a
 * merged model through fetchFileVersion), and React Native has no page URL: there it is none.
 *
 * ⚠️ KNOWN GAP, open, found 2026-11-02: a manual check no automated test covers (staging, logged in), as a
 * transmittal's destination-side recipient: a single file (its record, the File details panel, the Views panel, the
 * default view) and a transmitted merged model (its constituent records load, selection and properties work, and a
 * deleted-Space constituent shows its refusal once). Server side: fileOperations.mongo.service.ts getFileDetails.
 *
 * ⚠️ KNOWN BUG, open, found 2026-11-02: a transmitted file opened without the transmittal in the page URL (a search
 * hit, a pasted or bookmarked viewer link) is refused to a recipient who holds no View of their own on it: the page URL
 * is the record read's only source of the transmittal, so the read goes out without one and the viewer shows the 403
 * page ("This file is not available!"). Fix: every link that opens a transmitted file for a recipient carries its
 * `transmittalId`, or the server finds the caller's transmittal for the version when none is sent.
 */
export function viewerTransmittalId(): ObjectIdString | undefined {
  // Read off `globalThis`, not `window.location`: React Native defines no `location`.
  const pageLocation: unknown = Reflect.get(globalThis, "location");
  if (typeof pageLocation !== "object" || pageLocation === null || !("search" in pageLocation)) return undefined;
  const { search } = pageLocation;
  if (typeof search !== "string") return undefined;
  return objectIdString.safeParse(new URLSearchParams(search).get("transmittalId")).data;
}

/**
 * THE isFile403 call — reports the caller's permission map, whether the file is
 * deleted, and the same per constituent for a merged file.
 *
 * Issued once per file open, by the setAccessPermission thunk only. The result is
 * stored in the accessPermission slice and every other consumer reads it from
 * there, so there is deliberately no cache here.
 */
export async function resolveFileAccess(
  // Only `spaceId` is read, and setAccessPermission deliberately synthesises a one-entry map
  // holding just that — so the parameter is the structural minimum, not the full slice type.
  filesData: Record<string, { spaceId?: string }> | undefined,
  fileVersionId: string,
): Promise<ApiPostResponse<"/folders/isFile403">> {
  // Recipient share view (CP-283): permissions come from the LINK, not from a per-user ACL — there is
  // no account to resolve one against. Branching at this single choke point (the app's only isFile403
  // call) means every downstream gate — `setAccessPermission`, `viewableFileVersionIdsFrom`, the
  // viewer's load gates — is answered correctly for a recipient without any of them knowing about
  // sharing. It also avoids calling the authenticated route, which would 401 and could bounce the
  // recipient to the sign-in page.
  const shareSession = getShareViewerSession();
  if (shareSession) {
    const parsedVersionId = objectIdString.safeParse(fileVersionId);
    if (!parsedVersionId.success) {
      return { status: false, errorCode: errorCodes.VALIDATION_FAILED };
    }
    const shared = await getSharedFileAccess({
      token: shareSession.token,
      sessionToken: shareSession.sessionToken,
      fileVersionId: parsedVersionId.data,
    }).catch((error: unknown) => {
      Sentry.captureException(error);
      return null;
    });
    if (!shared || !shared.status || !shared.valid) {
      return { status: false, errorCode: errorCodes.EXTERNAL_LINK_INVALID };
    }
    // The recipient's 12h session token can expire MID-VIEW on a password-protected link. That
    // answer arrives as a successful envelope ({status:true, valid:true, requiresPassword:true})
    // carrying no permissions — letting it through here left the viewer permanently blank. Reported
    // as a plain failure instead: the viewer shows its restricted state, and the next navigation
    // re-resolves at page level, which owns the redirect back to the password gate.
    if (shared.requiresPassword) {
      return { status: false, errorCode: errorCodes.EXTERNAL_LINK_UNLOCK_REQUIRED };
    }
    return shared;
  }

  const transmittal = getTransmittalContext();
  const spaceId = transmittal?.spaceId ?? filesData?.[fileVersionId]?.spaceId;
  if (!fileVersionId || !spaceId) return { status: false, errorCode: errorCodes.VALIDATION_FAILED };

  try {
    const response = await axiosWrapper.api.post("/folders/isFile403", {
      fileVersionId,
      spaceId,
      includeConstituentAccess: true,
      ...(transmittal?.transmittalId ? { transmittalId: transmittal.transmittalId } : {}),
    });
    return response.data;
  } catch (error: unknown) {
    Sentry.captureException(error);
    return { status: false, errorCode: errorCodes.SOMETHING_WENT_WRONG };
  }
}

/**
 * Ids worth loading model data for: the versions the open file's access check lists as viewable. Falls back to the
 * un-gated list when the result carries no `files` — the check failed, or an older server ignored
 * includeConstituentAccess. The fallback is safe only for reads the server answers per version, refusing on its own
 * the versions the caller may not read. A read it refuses whole for one such version
 * (/queries/getDistinctFilePropertyNames) needs the check's answer, so it waits for it instead. Versions with no
 * model-tree archive are dropped too: the server refuses their property fetch with a body that is not a zip.
 */
export function viewableFileVersionIdsFrom(
  access: FileAccessResult | undefined,
  // Forwarded verbatim to resolveFileVersionIds and hasModelTreeKey, which own the entry's shape.
  filesData: Parameters<typeof resolveFileVersionIds>[0],
  fileVersionId: string,
): string[] {
  const ids =
    access?.status === true && access.files
      ? (access.viewableFileVersionIds ?? [])
      : resolveFileVersionIds(filesData, fileVersionId);
  return ids.filter((id) => hasModelTreeKey(filesData, fileVersionId, id));
}
